/**
 * sdfParametric.ts — typed wrappers for the 2D SDF library in
 * src/gl/rectPipeline.ts (which mirrors the Inigo Quilez formulas covered by
 * the rustadventure SDF tutorial: https://www.rustadventure.dev/introduction-to-signed-distance-fields).
 *
 * Every builder returns a [sdfKind, sdfParamsA, sdfParamsB] triple that's
 * directly assignable to a SceneNode's `sdfKind` / `sdfParamsA` / `sdfParamsB`
 * fields. Use them with `makeSdf` from sceneGraph.ts.
 *
 * Local-space convention (matches NSPP's rect pipeline):
 *   - Origin (0,0) is the rect's CENTER.
 *   - The rect's w/h form the visible bounds; the SDF may extend beyond.
 *   - +Y points DOWN (CSS / WebGL convention).
 *
 * For convenience the builders accept `bounds = { w, h }` so the parameters
 * land in a sensible scale regardless of the rect size.
 */

import type { SceneNode } from "./sceneGraph";

export type SdfKind = NonNullable<SceneNode["sdfKind"]>;
export type SdfParams = NonNullable<SceneNode["sdfParamsA"]>;

export interface SdfSpec {
  sdfKind: SdfKind;
  sdfParamsA: SdfParams;
  sdfParamsB: SdfParams;
}

// -------- Primitives ---------------------------------------------------------

/** Circle, centered. `r` is the radius in CSS px. */
export function sdfCircle(r: number): SdfSpec {
  return { sdfKind: "circle", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Rounded rectangle. `bounds.w/h` set the rect; `r` is corner radius. */
export function sdfRoundedBox(bounds: { w: number; h: number }, r: number): SdfSpec {
  // The pipeline reads `b` from vHalfSize (= bounds/2). We only need to pass
  // the corner radius here. The rect's w/h is implicit in the node bounds.
  return { sdfKind: "roundedBox", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
  // bounds used by the caller via makeSdf({ w: bounds.w, h: bounds.h, ... })
  void bounds;
}

/** Ring/annulus. `rOuter` is outer radius, `rInner` inner. */
export function sdfRing(rOuter: number, rInner: number): SdfSpec {
  return { sdfKind: "ring", sdfParamsA: [rOuter, rInner, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Capsule/pill. `a` and `b` are endpoints, `r` is the cap radius. */
export function sdfCapsule(
  a: [number, number],
  b: [number, number],
  r: number,
): SdfSpec {
  return {
    sdfKind: "capsule",
    sdfParamsA: [a[0], a[1], b[0], b[1]],
    sdfParamsB: [r, 0, 0, 0],
  };
}

/** Regular polygon. `n` sides (>= 3), `r` circumradius. */
export function sdfPolygon(n: number, r: number): SdfSpec {
  return { sdfKind: "polygon", sdfParamsA: [n, r, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** 5-point star. `r` outer radius, `rf` inner-radius factor (0..1). */
export function sdfStar(r: number, rf = 0.45): SdfSpec {
  return { sdfKind: "star", sdfParamsA: [r, rf, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Heart. `r` controls overall size. */
export function sdfHeart(r: number): SdfSpec {
  return { sdfKind: "heart", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

// -------- Curves & strokes ---------------------------------------------------

/** Circular arc. `r` ring radius, `th` half-thickness, `a` angular half-width. */
export function sdfArc(r: number, th: number, a: number): SdfSpec {
  return { sdfKind: "arc", sdfParamsA: [r, th, a, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Line segment with a half-thickness `th`. */
export function sdfSegment(
  a: [number, number],
  b: [number, number],
  th: number,
): SdfSpec {
  return {
    sdfKind: "segment",
    sdfParamsA: [a[0], a[1], b[0], b[1]],
    sdfParamsB: [th, 0, 0, 0],
  };
}

/** Quadratic bezier stroke from `a` → `b` with control `c`, half-thickness `th`. */
export function sdfBezier(
  a: [number, number],
  b: [number, number],
  c: [number, number],
  th: number,
): SdfSpec {
  return {
    sdfKind: "bezier",
    sdfParamsA: [a[0], a[1], b[0], b[1]],
    sdfParamsB: [c[0], c[1], th, 0],
  };
}

// -------- Polygons -----------------------------------------------------------

/** Rhombus / diamond. `b` is half-extents along the two diagonals. */
export function sdfRhombus(b: [number, number]): SdfSpec {
  return { sdfKind: "rhombus", sdfParamsA: [b[0], b[1], 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Trapezoid (axis-aligned). `w1` top width, `w2` bottom width, `h` total height. */
export function sdfTrapezoid(w1: number, w2: number, h: number): SdfSpec {
  return { sdfKind: "trapezoid", sdfParamsA: [w1, w2, h, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Pie / wedge. `c` center offset, `r` radius, `th` rim half-thickness, `a` angular half-width. */
export function sdfPie(
  c: [number, number],
  r: number,
  th: number,
  a: number,
): SdfSpec {
  return {
    sdfKind: "pie",
    sdfParamsA: [c[0], c[1], r, th],
    sdfParamsB: [a, 0, 0, 0],
  };
}

/** Horseshoe (open ring). `r` ring radius, `th` half-thickness, `w` slot half-width, `a` slot half-angle. */
export function sdfHorseshoe(r: number, th: number, w: number, a: number): SdfSpec {
  return { sdfKind: "horseshoe", sdfParamsA: [r, th, w, a], sdfParamsB: [0, 0, 0, 0] };
}

/** Oriented (rotated) box. `bounds.w/h` set the rect, `rot` is the rotation in radians. */
export function sdfOrientedBox(_bounds: { w: number; h: number }, rot: number): SdfSpec {
  return { sdfKind: "orientedBox", sdfParamsA: [rot, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
  void _bounds;
}

// -------- IQ 2D extras (added in v2) ----------------------------------------

/** IQ: equilateral triangle inscribed in a circle of radius r, point up. */
export function sdfEquilateralTriangle(r: number): SdfSpec {
  return { sdfKind: "equilateralTri", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: vesica piscis. `r` is the circle radius, `h` is the offset between centers. */
export function sdfVesica(r: number, h: number): SdfSpec {
  return { sdfKind: "vesica", sdfParamsA: [r, h, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: moon / crescent. Two circles of radii ra (wider) and rb (narrower), offset by h. */
export function sdfMoon(ra: number, rb: number, h: number): SdfSpec {
  return { sdfKind: "moon", sdfParamsA: [ra, rb, h, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: axis-aligned ellipse. `a` is the x semi-axis, `b` is the y semi-axis. */
export function sdfEllipse(a: number, b: number): SdfSpec {
  return { sdfKind: "ellipse", sdfParamsA: [a, b, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: plus-sign cross. `w` is the total half-width, `t` is the bar thickness. */
export function sdfCross(w: number, t: number): SdfSpec {
  return { sdfKind: "cross", sdfParamsA: [w, t, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: parallelogram defined by two edge vectors a and b from origin. */
export function sdfParallelogram(a: [number, number], b: [number, number]): SdfSpec {
  return { sdfKind: "parallelogram", sdfParamsA: [a[0], a[1], b[0], b[1]], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: hexagram (Star of David). `r` is the circumradius of each triangle. */
export function sdfHexagram(r: number): SdfSpec {
  return { sdfKind: "hexagram", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: bobbly cross. 5 circles — center + 4 endpoints. `w` arm length, `r` circle radius. */
export function sdfBobblyCross(w: number, r: number): SdfSpec {
  return { sdfKind: "bobblyCross", sdfParamsA: [w, r, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: simple egg. `ra` is the wider-end radius, `rb` is the narrower-end radius. */
export function sdfEgg(ra: number, rb: number): SdfSpec {
  return { sdfKind: "egg", sdfParamsA: [ra, rb, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: parabola segment. `w` is the half-width, `a` controls the curvature. */
export function sdfParabolaSegment(w: number, a: number): SdfSpec {
  return { sdfKind: "parabolaSeg", sdfParamsA: [w, a, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: stairs. `w` step width, `h` step height, `n` number of steps. */
export function sdfStairs(w: number, h: number, n: number): SdfSpec {
  return { sdfKind: "stairs", sdfParamsA: [w, h, n, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: circle wave (sinusoidal radius). `r` base radius, `amp` amplitude, `freq` frequency. */
export function sdfCircleWave(r: number, amp: number, freq: number): SdfSpec {
  return { sdfKind: "circleWave", sdfParamsA: [r, amp, freq, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: hyperbola. y = a/x. `a` is the hyperbola parameter. */
export function sdfHyperbola(a: number): SdfSpec {
  return { sdfKind: "hyperbola", sdfParamsA: [a, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: cool S (cubic Bezier S-curve). `a` is the half-width, `b` is the half-amplitude. */
export function sdfCoolS(a: number, b: number): SdfSpec {
  return { sdfKind: "coolS", sdfParamsA: [a, b, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: pentagram. `r` is the circumradius. */
export function sdfPentagram(r: number): SdfSpec {
  return { sdfKind: "pentagram", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: regular N-pointed star. `r` outer radius, `n` number of points, `rf` inner/outer ratio. */
export function sdfRegularStar(r: number, n: number, rf: number): SdfSpec {
  return { sdfKind: "regularStar", sdfParamsA: [r, n, rf, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: isosceles triangle. `w` base width, `h` height. */
export function sdfIsoscelesTri(w: number, h: number): SdfSpec {
  return { sdfKind: "isoscelesTri", sdfParamsA: [w, h, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: rounded X (X-shaped cross with rounded ends). `w` arm length, `t` bar thickness, `r` end radius. */
export function sdfRoundedX(w: number, t: number, r: number): SdfSpec {
  return { sdfKind: "roundedX", sdfParamsA: [w, t, r, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: circle cross (5-circle plus shape). `r` is the dot radius. */
export function sdfCircleCross(r: number): SdfSpec {
  return { sdfKind: "circleCross", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: oriented vesica (vesica with rotation). */
export function sdfOrientedVesica(r: number, h: number, rot: number): SdfSpec {
  return { sdfKind: "orientedVesica", sdfParamsA: [r, h, rot, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: parabola. `a` is the curvature parameter. */
export function sdfParabola(a: number): SdfSpec {
  return { sdfKind: "parabola", sdfParamsA: [a, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** IQ: quadratic circle. `r` is the radius. */
export function sdfQuadraticCircle(r: number): SdfSpec {
  return { sdfKind: "quadraticCircle", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

// -------- New inventions (not in IQ's library) --------------------------------

/** Squircle (iOS-style superellipse). |x|^n + |y|^n = r^n. n=2: circle, n=4: squircle. */
export function sdfSquircle(r: number, n: number): SdfSpec {
  return { sdfKind: "squircle", sdfParamsA: [r, n, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Gear with N teeth. `r` pitch radius, `n` number of teeth, `h` tooth height. */
export function sdfGear(r: number, n: number, h: number): SdfSpec {
  return { sdfKind: "gear", sdfParamsA: [r, n, h, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Star with N long thin spikes. `r` base radius, `n` number of spikes. */
export function sdfSpike(r: number, n: number): SdfSpec {
  return { sdfKind: "spike", sdfParamsA: [r, n, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Plus sign with smooth corners. `w` half-arm-length, `r` corner radius. */
export function sdfPlus(w: number, r: number): SdfSpec {
  return { sdfKind: "plus", sdfParamsA: [w, r, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Fluffy cloud (5 overlapping circles). `r` is the overall radius. */
export function sdfCloud(r: number): SdfSpec {
  return { sdfKind: "cloud", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Lightning bolt. `h` is the bolt's half-height. */
export function sdfLightning(h: number): SdfSpec {
  return { sdfKind: "lightning", sdfParamsA: [h, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Speech bubble. `w` body half-width, `h` body half-height, `r` corner radius. */
export function sdfSpeechBubble(w: number, h: number, r: number): SdfSpec {
  return { sdfKind: "speechBubble", sdfParamsA: [w, h, r, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Heart (alias — same as regular heart for now). */
export function sdfHeartBroken(r: number): SdfSpec {
  return { sdfKind: "heartBroken", sdfParamsA: [r, 0, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Dumbbell. `w` bar length, `h` bar half-thickness, `r` end-cap radius. */
export function sdfDumbbell(w: number, h: number, r: number): SdfSpec {
  return { sdfKind: "dumbbell", sdfParamsA: [w, h, r, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Flower with N petals. `r` is the overall radius, `n` number of petals. */
export function sdfFlower(r: number, n: number): SdfSpec {
  return { sdfKind: "flower", sdfParamsA: [r, n, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Shield shape (rounded top, pointed bottom). `w` half-width, `h` half-height. */
export function sdfShield(w: number, h: number): SdfSpec {
  return { sdfKind: "shield", sdfParamsA: [w, h, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Bookmark shape (rectangle with notch at bottom). `w` half-width, `h` half-height. */
export function sdfBookmark(w: number, h: number): SdfSpec {
  return { sdfKind: "bookmark", sdfParamsA: [w, h, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Right-pointing arrow. `w` half-width, `h` half-height. */
export function sdfArrow(w: number, h: number): SdfSpec {
  return { sdfKind: "arrow", sdfParamsA: [w, h, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Cog (gear with hole). `r` outer radius, `n` teeth, `h` tooth height, `holeR` hole radius. */
export function sdfCog(r: number, n: number, h: number, holeR: number): SdfSpec {
  return { sdfKind: "cog", sdfParamsA: [r, n, h, holeR], sdfParamsB: [0, 0, 0, 0] };
}

/** Wavy tape strip. `w` half-width, `amp` wave amplitude, `freq` wave frequency. */
export function sdfTapeStrip(w: number, amp: number, freq: number): SdfSpec {
  return { sdfKind: "tapeStrip", sdfParamsA: [w, amp, freq, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Asterisk shape (3 crossing line segments). `r` arm half-length, `t` arm half-thickness. */
export function sdfAsterisk(r: number, t: number): SdfSpec {
  return { sdfKind: "asterisk", sdfParamsA: [r, t, 0, 0], sdfParamsB: [0, 0, 0, 0] };
}

/** Archimedean spiral. `r1` inner radius, `r2` outer radius, `turns` number of turns, `th` line thickness. */
export function sdfSpiral(r1: number, r2: number, turns: number, th: number): SdfSpec {
  return { sdfKind: "spiral", sdfParamsA: [r1, r2, turns, th], sdfParamsB: [0, 0, 0, 0] };
}

// -------- Library metadata ---------------------------------------------------

export interface SdfEntry {
  kind: SdfKind;
  name: string;
  formula: string;
  builder: (...args: any[]) => SdfSpec;
  /** Hint about which rustadventure tutorial section this comes from. */
  note: string;
}

/**
 * The complete SDF library exposed as a metadata table. Scenes can iterate
 * over this to render the entire catalogue, and developers can see the exact
 * formula + parameter signature for every shape.
 */
export const SDF_LIBRARY: readonly SdfEntry[] = [
  { kind: "circle", name: "circle", formula: "length(p) - r",
    builder: sdfCircle, note: "rustadventure: SDF Primitives — Circle" },
  { kind: "roundedBox", name: "rounded box", formula: "sdBox(p,b) - r",
    builder: sdfRoundedBox, note: "rustadventure: SDF Primitives — Box (with rounding)" },
  { kind: "ring", name: "ring", formula: "abs(length(p) - midR) - halfW",
    builder: sdfRing, note: "rustadventure: Combining Primitives — Ring" },
  { kind: "capsule", name: "capsule", formula: "sdSegment(p,a,b) - r",
    builder: sdfCapsule, note: "rustadventure: Combining Primitives — Capsule" },
  { kind: "polygon", name: "regular polygon", formula: "n-gon, circumradius r",
    builder: sdfPolygon, note: "rustadventure: Polygons — Regular n-gon" },
  { kind: "star", name: "5-point star", formula: "min/max over 5 sector normals",
    builder: sdfStar, note: "rustadventure: Polygons — Star" },
  { kind: "heart", name: "heart", formula: "parametric polar distance",
    builder: sdfHeart, note: "rustadventure: Polygons — Heart" },
  { kind: "arc", name: "arc", formula: "sdRing clipped to angular sector",
    builder: sdfArc, note: "rustadventure: Arc / pie slice" },
  { kind: "segment", name: "line segment", formula: "length(pa - ba*h) - th",
    builder: sdfSegment, note: "rustadventure: Curves — Line segment" },
  { kind: "bezier", name: "quadratic bezier", formula: "Newton iterations on closest point",
    builder: sdfBezier, note: "rustadventure: Curves — Quadratic bezier" },
  { kind: "rhombus", name: "rhombus / diamond", formula: "d*sign(ndot(p,b))",
    builder: sdfRhombus, note: "rustadventure: Rhombus / diamond" },
  { kind: "trapezoid", name: "trapezoid", formula: "max of 4 half-planes",
    builder: sdfTrapezoid, note: "rustadventure: Trapezoid" },
  { kind: "pie", name: "pie / wedge", formula: "sdRing clipped to angle",
    builder: sdfPie, note: "rustadventure: Pie slice" },
  { kind: "horseshoe", name: "horseshoe", formula: "sdRing clipped by 2 face half-planes",
    builder: sdfHorseshoe, note: "rustadventure: Horseshoe" },
  { kind: "orientedBox", name: "oriented box", formula: "sdBox(rot^-1 * p, b)",
    builder: sdfOrientedBox, note: "rustadventure: Oriented box" },
  { kind: "equilateralTri", name: "equilateral triangle", formula: "3-fold symmetric fold",
    builder: sdfEquilateralTriangle, note: "IQ 2D: equilateral triangle" },
  { kind: "vesica", name: "vesica piscis", formula: "max(d1, d2) of two offset circles",
    builder: sdfVesica, note: "IQ 2D: intersection of two circles" },
  { kind: "moon", name: "crescent moon", formula: "max(dA, -dB) symmetric difference",
    builder: sdfMoon, note: "IQ 2D: crescent / moon" },
  { kind: "ellipse", name: "ellipse", formula: "(|p/a|^2 + |p/b|^2) - 1 approx",
    builder: sdfEllipse, note: "IQ 2D: axis-aligned ellipse" },
  { kind: "cross", name: "plus cross", formula: "min of two overlapping rectangles",
    builder: sdfCross, note: "IQ 2D: plus-sign cross" },
  { kind: "parallelogram", name: "parallelogram", formula: "edge projection + winding",
    builder: sdfParallelogram, note: "IQ 2D: parallelogram from two edge vectors" },
  { kind: "hexagram", name: "star of david", formula: "min of two equilateral triangles",
    builder: sdfHexagram, note: "IQ 2D: hexagram / Star of David" },
  { kind: "bobblyCross", name: "bobbly cross", formula: "min of 5 circles",
    builder: sdfBobblyCross, note: "IQ 2D: bobbly cross" },
  { kind: "egg", name: "egg", formula: "asymmetric equilateral-triangle fold",
    builder: sdfEgg, note: "IQ 2D: simple egg" },
  { kind: "stairs", name: "stairs", formula: "modulo'd square minus center rect",
    builder: sdfStairs, note: "IQ 2D: staircase" },
  { kind: "circleWave", name: "wavy circle", formula: "length(p) - r - amp*sin(theta*freq)",
    builder: sdfCircleWave, note: "IQ 2D: circle wave" },
  { kind: "pentagram", name: "pentagram", formula: "5-point star, ratio 0.382",
    builder: sdfPentagram, note: "IQ 2D: 5-pointed star" },
  { kind: "regularStar", name: "regular N-star", formula: "generalized 2N-vertex star",
    builder: sdfRegularStar, note: "IQ 2D: regular N-pointed star" },
  { kind: "squircle", name: "squircle", formula: "(|x|^n + |y|^n)^(1/n) - r",
    builder: sdfSquircle, note: "NEW: iOS-style superellipse" },
  { kind: "gear", name: "gear", formula: "circle modulated by cos(N*theta) teeth",
    builder: sdfGear, note: "NEW: gear with N teeth" },
  { kind: "spike", name: "spike star", formula: "circle modulated by |cos(N*theta/2)|^8",
    builder: sdfSpike, note: "NEW: star with N long thin spikes" },
  { kind: "plus", name: "rounded plus", formula: "rounded box minus 4 corner circles",
    builder: sdfPlus, note: "NEW: plus with smooth corners" },
  { kind: "roundedX", name: "rounded X", formula: "two rounded bars in X arrangement",
    builder: sdfRoundedX, note: "IQ 2D: X-shape with rounded ends" },
  { kind: "circleCross", name: "circle cross", formula: "5 overlapping circles in plus shape",
    builder: sdfCircleCross, note: "IQ 2D: plus made of 5 circles" },
  { kind: "isoscelesTri", name: "isosceles triangle", formula: "two slanted sides + horizontal base",
    builder: sdfIsoscelesTri, note: "IQ 2D: isosceles triangle" },
  { kind: "orientedVesica", name: "oriented vesica", formula: "vesica rotated by rot",
    builder: sdfOrientedVesica, note: "IQ 2D: vesica with rotation" },
  { kind: "parabola", name: "parabola", formula: "y - x*x/(4a) — algebraic",
    builder: sdfParabola, note: "IQ 2D: parabola curve" },
  { kind: "quadraticCircle", name: "quadratic circle", formula: "abs(length(p) - r)",
    builder: sdfQuadraticCircle, note: "IQ 2D: circle with quadratic SDF" },
  { kind: "cloud", name: "cloud", formula: "union of 5 overlapping circles",
    builder: sdfCloud, note: "NEW: 5-circle cloud silhouette" },
  { kind: "lightning", name: "lightning bolt", formula: "6-vertex polygon with zigzag",
    builder: sdfLightning, note: "NEW: zigzag lightning bolt" },
  { kind: "speechBubble", name: "speech bubble", formula: "rounded rect + tail triangle",
    builder: sdfSpeechBubble, note: "NEW: speech bubble with tail" },
  { kind: "dumbbell", name: "dumbbell", formula: "rectangle + 2 end-cap circles",
    builder: sdfDumbbell, note: "NEW: barbell/dumbbell" },
  { kind: "flower", name: "flower", formula: "center + n petal circles",
    builder: sdfFlower, note: "NEW: N-petal flower" },
  { kind: "shield", name: "shield", formula: "5-vertex polygon (rounded top, pointed bottom)",
    builder: sdfShield, note: "NEW: shield" },
  { kind: "bookmark", name: "bookmark", formula: "5-vertex polygon with notch",
    builder: sdfBookmark, note: "NEW: bookmark tag" },
  { kind: "arrow", name: "arrow", formula: "7-vertex polygon (rect + triangle)",
    builder: sdfArrow, note: "NEW: right-pointing arrow" },
  { kind: "cog", name: "cog", formula: "gear teeth ring minus central hole",
    builder: sdfCog, note: "NEW: cog/gear with central hole" },
  { kind: "tapeStrip", name: "tape strip", formula: "between two parallel wavy lines",
    builder: sdfTapeStrip, note: "NEW: wavy tape strip" },
  { kind: "asterisk", name: "asterisk", formula: "3 rotated rectangles",
    builder: sdfAsterisk, note: "NEW: asterisk (3 line segments)" },
  { kind: "spiral", name: "spiral", formula: "Archimedean spiral r1 to r2",
    builder: sdfSpiral, note: "NEW: Archimedean spiral" },
] as const;
